import { ISelectItem } from "~/components/Controls/Select";
import { stringDateFormat } from "~/lib/date";
import { AccountCondition } from "~/modules/Clients/models/AccountInfo";

const netConditionValues = Object.values(AccountCondition);
const netConditionSet = new Set<AccountCondition>(netConditionValues);

export const netConditions: ISelectItem<AccountCondition>[] = netConditionValues.map((value) => ({
  value,
  label: value.toUpperCase(),
}));

export const parseNetCondition = (net?: string | null): AccountCondition | null => {
  if (!net) {
    return null;
  }
  return netConditionSet.has(net as AccountCondition) ? (net as AccountCondition) : null;
};

export const formatNetLabel = (net?: AccountCondition | string | null): string => {
  if (!net) {
    return "—";
  }
  return net.toUpperCase();
};

const isFinanceIntervalRange = (value: unknown): value is FinanceIntervalRange => {
  if (!value || typeof value !== "object") {
    return false;
  }

  return (
    "start" in value &&
    "end" in value &&
    typeof (value as FinanceIntervalRange).start === "string" &&
    typeof (value as FinanceIntervalRange).end === "string"
  );
};

const parseDateMs = (value: string): number | null => {
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

export const formatIntervalCovered = (
  value: FinanceIntervalRange | FinanceIntervalRange[] | string | null | undefined,
  dateFormat: Intl.DateTimeFormatOptions
): string => {
  const intervals = Array.isArray(value)
    ? value.filter(isFinanceIntervalRange)
    : isFinanceIntervalRange(value)
      ? [value]
      : [];

  if (intervals.length) {
    const labels = intervals.map(
      (interval) => `${stringDateFormat(interval.start, dateFormat)} - ${stringDateFormat(interval.end, dateFormat)}`
    );
    return labels.length > 1 ? `${labels[0]} +${labels.length - 1} more` : labels[0];
  }

  if (typeof value !== "string") {
    return "—";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "—";
  }

  const dateParts = trimmed.match(/\d{4}-\d{2}-\d{2}(?:[T ][0-9:.+-]+Z?)?/g) ?? [];
  if (dateParts.length >= 2) {
    const [start, end] = dateParts;
    if (parseDateMs(start) !== null && parseDateMs(end) !== null) {
      return `${stringDateFormat(start, dateFormat)} - ${stringDateFormat(end, dateFormat)}`;
    }
  }
  if (dateParts.length === 1) {
    const [dateValue] = dateParts;
    if (parseDateMs(dateValue) !== null) {
      return stringDateFormat(dateValue, dateFormat);
    }
  }

  return trimmed;
};

export type FinanceIntervalRange = {
  start: string;
  end: string;
};

export interface IFinanceSummary {
  accountId: number;
  sellerName?: string | null;
  net?: AccountCondition | null;
  payoutDate?: string | null;
  intervalCovered?: FinanceIntervalRange | FinanceIntervalRange[] | string | null;
  scheduledAmount?: number | null;
}
